/**
 * AD/BC modification (May 2026)
 *
 * Two changes layered onto Dawn's media-gallery:
 *
 * 1. Variant-image visibility toggle. The Liquid snippet renders variant-
 *    linked slides with inline display:none. This file reveals the matching
 *    slide whenever a variant is activated, and re-hides previously-shown
 *    variant slides. Three triggers cover whichever path Dawn's variant
 *    change handler uses:
 *      a. setActiveMedia hook — for themes that call it directly
 *      b. optionValueSelectionChange subscription — for themes that section-
 *         swap silently or otherwise don't call setActiveMedia
 *      c. handleUrlPreselection in connectedCallback — for initial page load
 *         (Google Shopping / Meta retargeting) and for post-section-swap
 *         element reconstruction
 *
 * 2. Variant-required ATC gate. Until the customer actively selects a variant,
 *    the Add to Cart button is disabled and reads "Choose your phone model". Skips
 *    automatically on single-variant products (no <variant-selects> element)
 *    and on pages with ?variant=X already in the URL.
 */

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        // AD/BC: subscribe to variant change events so we can swap the visible
        // gallery image even if Dawn's product-info handler doesn't call
        // setActiveMedia directly.
        if (
          typeof subscribe !== 'undefined' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.optionValueSelectionChange
        ) {
          this._variantChangeUnsubscribe = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleVariantChangeEvent.bind(this)
          );
        }

        // AD/BC: initial-load and post-section-swap variant resolution.
        this.handleUrlPreselection();
      }

      disconnectedCallback() {
        if (this._variantChangeUnsubscribe) this._variantChangeUnsubscribe();
      }

      handleUrlPreselection() {
        const params = new URLSearchParams(window.location.search);
        const variantId = params.get('variant');
        if (!variantId) return;
        this.activateVariantById(variantId);
      }

      handleVariantChangeEvent() {
        // Dawn updates window.location.search shortly after this event fires.
        // Wait one tick, then resolve the new variant ID from the URL.
        setTimeout(() => {
          const variantId = new URLSearchParams(window.location.search).get('variant');
          if (variantId) this.activateVariantById(variantId);
        }, 150);
      }

      async activateVariantById(variantId) {
        try {
          const data = await this.fetchProductData();
          if (!data || !Array.isArray(data.variants)) return;
          const variant = data.variants.find((v) => String(v.id) === String(variantId));
          if (!variant || !variant.featured_media) return;
          const sectionId = this.dataset.sectionId;
          if (!sectionId) return;
          const mediaId = `${sectionId}-${variant.featured_media.id}`;
          this.setActiveMedia(mediaId, true);
        } catch (e) {
          console.error('media-gallery: activateVariantById failed', e);
        }
      }

      fetchProductData() {
        if (this._productDataPromise) return this._productDataPromise;
        const handle = this.dataset.productHandle;
        if (!handle) return Promise.resolve(null);
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        this._productDataPromise = fetch(`${root}products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch((e) => {
            console.error('media-gallery: failed to fetch product data', e);
            return null;
          });
        return this._productDataPromise;
      }

      toggleVariantSlideVisibility(activeMediaId) {
        // Reveal the active variant slide; hide all other variant slides.
        if (this.elements.viewer) {
          this.elements.viewer.querySelectorAll('[data-variant-media="true"]').forEach((el) => {
            if (el.dataset.mediaId === activeMediaId) {
              el.style.display = '';
            } else {
              el.style.display = 'none';
              el.classList.remove('is-active');
            }
          });
        }
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-variant-media="true"]').forEach((el) => {
            if (el.dataset.target === activeMediaId) {
              el.style.display = '';
            } else {
              el.style.display = 'none';
            }
          });
        }
        // Recalculate slider pagination after visibility change.
        if (this.elements.viewer && this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
        if (this.elements.thumbnails && typeof this.elements.thumbnails.resetPages === 'function') {
          this.elements.thumbnails.resetPages();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // AD/BC: toggle variant slide visibility before activation runs.
        this.toggleVariantSlideVisibility(mediaId);

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/**
 * AD/BC: Variant-required Add to Cart gate.
 *
 * Disables the Add to Cart button on multi-variant products until the customer
 * actively selects a variant. Skips automatically on single-variant products
 * (where <variant-selects> doesn't render) and on pages that arrive with a
 * pre-selected variant in the URL.
 */
(function initVariantRequiredGate() {
  const PROMPT_TEXT = 'Choose your phone model';

  const apply = () => {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return; // single-variant product, no gate needed

    const productForms = document.querySelectorAll('product-form');
    if (!productForms.length) return;

    const gatedButtons = [];

    productForms.forEach((form) => {
      const button = form.querySelector('button[type="submit"][name="add"]');
      if (!button) return;

      const originalHTML = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.adbcVariantGate = 'locked';
      button.innerHTML = `<span>${PROMPT_TEXT}</span>`;

      gatedButtons.push({ button, originalHTML });
    });

    if (!gatedButtons.length) return;

    const release = () => {
      gatedButtons.forEach(({ button, originalHTML }) => {
        if (button.dataset.adbcVariantGate !== 'locked') return;
        button.innerHTML = originalHTML;
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.adbcVariantGate;
      });
    };

    if (
      typeof subscribe !== 'undefined' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.optionValueSelectionChange
    ) {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, release);
    }

    // Click on the already-selected variant: radios don't fire change when
    // their checked value doesn't change, so we listen for clicks within the
    // variant-selects element to capture the "I want the auto-selected one"
    // confirmation case.
    variantSelects.addEventListener('click', (event) => {
      if (event.target.closest('input[type="radio"], label, select, option, button')) {
        release();
      }
    });

    if (new URLSearchParams(window.location.search).has('variant')) {
      release();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();
